import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDownToLine, Plus } from "lucide-react";

// Files on a client's private page: exports, examples, a recording of the job.
// A file goes up in pieces, so a long video survives a weak connection: a
// piece that fails is sent again, not the whole file.

const AT_ONCE = 3; // pieces of one file in flight together
const TRIES = 4;

const size = (n) =>
  n >= 1024 ** 3
    ? `${(n / 1024 ** 3).toFixed(1)} GB`
    : n >= 1024 ** 2
      ? `${(n / 1024 ** 2).toFixed(n < 10 * 1024 ** 2 ? 1 : 0)} MB`
      : n >= 1024
        ? `${Math.max(1, Math.round(n / 1024))} KB`
        : `${n} B`;
const when = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
const whose = (file, me) =>
  file.ours
    ? "Nomus"
    : !file.by || file.by === me?.email
      ? "You"
      : file.by.split("@")[0];
const wait = (ms) => new Promise((r) => window.setTimeout(r, ms));

async function ask(action, payload) {
  const response = await fetch(`/api/room/file/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw Object.assign(new Error(body.error || ""), {
      status: response.status,
    });
  return body;
}
// One piece, with its progress as it goes. fetch cannot report that.
function sendPiece(url, blob, onSent, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const stop = () => xhr.abort();
    signal.addEventListener("abort", stop, { once: true });
    const end = (fn, value) => {
      signal.removeEventListener("abort", stop);
      fn(value);
    };
    xhr.open("POST", url);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (e) => onSent(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return end(resolve);
      let message = "";
      try {
        message = JSON.parse(xhr.responseText).error || "";
      } catch {
        // not JSON: the status says enough
      }
      end(reject, Object.assign(new Error(message), { status: xhr.status }));
    };
    xhr.onerror = () => end(reject, new Error(""));
    xhr.onabort = () =>
      end(reject, Object.assign(new Error(""), { stopped: true }));
    xhr.send(blob);
  });
}
async function sendFile(slug, file, { onStart, onProgress, signal }) {
  const start = await ask("start", { slug, name: file.name, size: file.size });
  onStart(start.id);
  const sent = new Array(start.pieces).fill(0);
  let next = 1;
  const lane = async () => {
    while (next <= start.pieces) {
      const n = next++;
      const blob = file.slice(
        (n - 1) * start.piece,
        Math.min(n * start.piece, file.size),
      );
      const url = `/api/room/file/piece?slug=${encodeURIComponent(slug)}&id=${start.id}&n=${n}`;
      for (let attempt = 1; ; attempt++) {
        try {
          await sendPiece(
            url,
            blob,
            (bytes) => {
              sent[n - 1] = bytes;
              onProgress(sent.reduce((a, b) => a + b, 0) / file.size);
            },
            signal,
          );
          sent[n - 1] = blob.size;
          break;
        } catch (e) {
          // A refusal is final; a dropped connection is worth another go.
          const refused = e.status && e.status < 500 && e.status !== 429;
          if (signal.aborted || refused || attempt >= TRIES) throw e;
          sent[n - 1] = 0;
          await wait(1500 * 2 ** (attempt - 1));
        }
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(AT_ONCE, start.pieces) }, lane),
  );
  return ask("done", { slug, id: start.id });
}

let serial = 0;

export default function RoomFiles({ room, closed }) {
  const slug = room.slug;
  const [files, setFiles] = useState(null);
  const [limits, setLimits] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [over, setOver] = useState(false);
  const [asking, setAsking] = useState(null);
  const [failed, setFailed] = useState("");
  const input = useRef(null);
  const queue = useRef([]);
  const running = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    ask("list", { slug })
      .then((r) => {
        if (!alive.current) return;
        setFiles(r.files);
        setLimits(r.limits);
      })
      .catch(() => alive.current && setFiles([]));
    return () => {
      alive.current = false;
      for (const job of queue.current) job.stop.abort();
    };
  }, [slug]);

  const patch = useCallback(
    (key, change) =>
      alive.current &&
      setJobs((list) =>
        list.map((j) => (j.key === key ? { ...j, ...change } : j)),
      ),
    [],
  );
  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    while (queue.current.length) {
      const job = queue.current[0];
      if (job.stop.signal.aborted) {
        queue.current.shift();
        continue;
      }
      let id = "";
      try {
        patch(job.key, { state: "sending" });
        const done = await sendFile(slug, job.file, {
          onStart: (made) => (id = made),
          onProgress: (p) => patch(job.key, { progress: p }),
          signal: job.stop.signal,
        });
        if (alive.current) {
          setFiles(done.files);
          setJobs((list) => list.filter((j) => j.key !== job.key));
        }
      } catch (e) {
        // Whatever arrived of it is cleared away again.
        if (id) ask("remove", { slug, id }).catch(() => {});
        if (job.stop.signal.aborted)
          alive.current &&
            setJobs((list) => list.filter((j) => j.key !== job.key));
        else
          patch(job.key, {
            state: "failed",
            error:
              e.message ||
              "That did not go through. Check the connection and add it again.",
          });
      }
      queue.current.shift();
    }
    running.current = false;
  }, [slug, patch]);

  const add = (list) => {
    const picked = [...(list || [])];
    if (!picked.length) return;
    setFailed("");
    const fresh = picked.map((file) => {
      const kind = (
        /\.([a-z0-9]{1,8})$/i.exec(file.name)?.[1] || ""
      ).toLowerCase();
      const error =
        limits && !limits.kinds.includes(kind)
          ? "That kind of file cannot be sent here. Documents, sheets, pictures, sound, video and zip archives can."
          : limits && file.size > limits.file
            ? "That file is over 5 GB. Write to hello@nomus.work and we find another way."
            : file.size === 0
              ? "That file is empty."
              : "";
      return {
        key: `job-${++serial}`,
        file,
        name: file.name,
        size: file.size,
        progress: 0,
        state: error ? "failed" : "waiting",
        error,
        stop: new AbortController(),
      };
    });
    setJobs((list) => [...list, ...fresh]);
    queue.current.push(...fresh.filter((j) => !j.error));
    run();
  };
  const cancel = (job) => {
    const gone = () => setJobs((list) => list.filter((j) => j.key !== job.key));
    const queued = queue.current.find((j) => j.key === job.key);
    if (!queued) return gone();
    // Under way: stopping it clears it. Still waiting: it just leaves the line.
    if (queue.current[0] === queued) return queued.stop.abort();
    queue.current = queue.current.filter((j) => j !== queued);
    return gone();
  };
  const remove = async (id) => {
    setFailed("");
    try {
      setFiles((await ask("remove", { slug, id })).files);
    } catch (e) {
      setFailed(e.message || "That was not removed. Try again in a moment.");
    }
    setAsking(null);
  };

  // Leaving mid-upload loses it: the browser asks first.
  const busy = jobs.some((j) => j.state === "waiting" || j.state === "sending");
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  if (files === null) return null;
  // A closed Audit keeps what was sent, and takes nothing more.
  if (closed && !files.length) return null;
  const count = files.length;

  return (
    <section
      className="px-5 pt-16 lg:pt-24 print:hidden"
      data-testid="room-files"
    >
      <div className="mx-auto grid max-w-[1400px] gap-x-8 gap-y-8 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <h2 className="nm-title text-[30px] leading-[1.05] sm:text-[44px]">
            Files
            {count > 0 && (
              <span className="ml-3 align-middle font-sans text-[14px] tabular-nums opacity-35">
                {count}
              </span>
            )}
          </h2>
          <p className="mt-4 max-w-[380px] text-[16px] leading-[1.45] text-black/60">
            {closed
              ? "What was sent during the Audit. It stays here to download."
              : "An export, a filled-in example, a screen recording of the job being done. Whatever shows us the work as it really is."}
          </p>
          {!closed && (
            <p className="mt-3 max-w-[380px] text-[13px] leading-[1.45] text-black/45">
              Only the people on this page and Nomus can open them. Up to 5 GB
              each. Never a file of passwords.
            </p>
          )}
        </div>

        <div className="lg:col-span-7 lg:col-start-6">
          {!closed && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                add(e.dataTransfer.files);
              }}
              className={`flex flex-wrap items-center justify-between gap-4 rounded-[20px] p-5 transition-colors sm:p-6 ${over ? "bg-black text-white" : "bg-[#f4f4f2]"}`}
            >
              <p className={`text-[16px] ${over ? "" : "text-black/60"}`}>
                {over ? "Let go to send." : "Drop files here, or choose them."}
              </p>
              <input
                ref={input}
                type="file"
                multiple
                className="sr-only"
                aria-label="Choose files to send"
                data-testid="room-files-input"
                onChange={(e) => {
                  add(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => input.current?.click()}
                className={`inline-flex h-10 items-center gap-2 rounded-full px-5 text-[13px] font-medium transition-colors ${over ? "bg-white text-black" : "bg-black text-white hover:bg-[#262626]"}`}
              >
                <Plus size={15} aria-hidden="true" />
                Add files
              </button>
            </div>
          )}

          {failed && (
            <p role="alert" className="mt-4 text-[14px] text-red-600">
              {failed}
            </p>
          )}

          {(jobs.length > 0 || count > 0) && (
            <ul className={`border-t border-black/10 ${closed ? "" : "mt-6"}`}>
              {jobs.map((job) => (
                <li
                  key={job.key}
                  className="border-b border-black/10 py-4"
                  data-testid="room-file-job"
                >
                  <div className="flex items-baseline justify-between gap-6">
                    <p className="min-w-0 truncate text-[17px] tracking-[-0.01em]">
                      {job.name}
                    </p>
                    <button
                      type="button"
                      onClick={() => cancel(job)}
                      className="shrink-0 text-[13px] text-black/50 underline-offset-4 hover:text-black hover:underline"
                    >
                      {job.state === "failed" ? "Dismiss" : "Cancel"}
                    </button>
                  </div>
                  {job.state === "failed" ? (
                    <p role="alert" className="mt-1 text-[14px] text-red-600">
                      {job.error}
                    </p>
                  ) : (
                    <>
                      <div
                        className="mt-3 h-[5px] overflow-hidden rounded-full bg-black/10"
                        role="progressbar"
                        aria-label={`Sending ${job.name}`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(job.progress * 100)}
                      >
                        <div
                          className="h-full rounded-full bg-black transition-[width] duration-300"
                          style={{
                            width: `${Math.round(job.progress * 100)}%`,
                          }}
                        />
                      </div>
                      <p className="mt-2 text-[13px] tabular-nums text-black/50">
                        {job.state === "waiting"
                          ? `Waiting its turn · ${size(job.size)}`
                          : job.progress >= 1
                            ? "Putting it together…"
                            : `${Math.round(job.progress * 100)}% of ${size(job.size)} · keep this page open`}
                      </p>
                    </>
                  )}
                </li>
              ))}
              {files.map((file) => (
                <li
                  key={file.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-black/10 py-4"
                  data-testid="room-file"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[17px] tracking-[-0.01em]">
                      {file.name}
                    </p>
                    <p className="mt-0.5 text-[13px] tabular-nums text-black/50">
                      {size(file.size)} · {whose(file, room.me)} ·{" "}
                      {when(file.at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-5 text-[13px]">
                    {asking === file.id ? (
                      <>
                        <span className="text-black/50">Remove it?</span>
                        <button
                          type="button"
                          onClick={() => remove(file.id)}
                          className="font-medium underline underline-offset-4"
                        >
                          Yes, remove
                        </button>
                        <button
                          type="button"
                          onClick={() => setAsking(null)}
                          className="text-black/50 hover:text-black"
                        >
                          Keep
                        </button>
                      </>
                    ) : (
                      <>
                        <a
                          href={`/api/room/file?slug=${encodeURIComponent(slug)}&id=${file.id}`}
                          download={file.name}
                          className="inline-flex items-center gap-1.5 font-medium underline-offset-4 hover:underline"
                        >
                          <ArrowDownToLine size={14} aria-hidden="true" />
                          Download
                        </a>
                        {!closed && !file.ours && (
                          <button
                            type="button"
                            onClick={() => setAsking(file.id)}
                            className="text-black/50 underline-offset-4 hover:text-black hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
